<template>
  <panel-navigation>
    <main class="affiliate-page">
      <h1 class="text-lg font-semibold mb-3">Refer &amp; Earn</h1>
      <hr class="border-b border-gray-100 mb-3">
      <div class="mb-3"><video-guide ids="referrals" label="Referral program walkthrough" /></div>
      <div class="affiliate-intro">
        <i class="mdi mdi-gift-outline" aria-hidden="true"></i>
        <div><strong>Earn 15% recurring commission</strong><p>Refer an agency to LSA Command and earn a commission on their qualifying client payments, for as long as they keep paying.</p></div>
      </div>
      <section v-if="loading" class="affiliate-card" role="status">Preparing your referral account…</section>
      <section v-else-if="error" class="affiliate-card" role="alert">
        <p>{{ error }}</p><button class="affiliate-button" @click="load">Try again</button>
      </section>
      <template v-else>
        <section class="affiliate-card">
          <h2>Your personal referral link</h2>
          <p>Share this link with agency owners. It opens a dedicated page on lsacommand.com where they can learn about LSA Command and create their agency account. Your referral stays connected, even if you use a white-label domain.</p>
          <div class="affiliate-copy">
            <input aria-label="Your referral link" :value="account.referral_link" readonly @focus="$event.target.select()" />
            <button class="affiliate-button" v-clipboard:copy="account.referral_link" v-clipboard:success="onCopied">{{ copied ? 'Copied!' : 'Copy link' }}</button>
          </div>
          <p class="affiliate-note">This link is for referring agencies to LSA Command. Keep using your usual client signup link for your own clients.</p>
        </section>
        <section class="affiliate-card affiliate-portal">
          <div><h2>Your earnings &amp; payouts</h2><p>See referred customers, commission balances, and payout options in your FirstPromoter portal.</p>
            <p v-if="!account.automatic_login" class="affiliate-note">Sign in with your agency owner email. If you have not set a password, use the portal’s password reset option.</p>
          </div>
          <button class="affiliate-button" :disabled="opening" @click="openPortal">{{ opening ? 'Opening…' : 'Open earnings portal ↗' }}</button>
        </section>
        <p class="affiliate-note">Commissions are based on successful payments. Refunds and disputed payments adjust your balance. Referrals to your own agency do not qualify.</p>
      </template>
    </main>
  </panel-navigation>
</template>

<script>
import axios from 'axios'
import PanelNavigation from '@/components/PanelNavigation.vue'

export default {
  components: { PanelNavigation },
  metaInfo: { title: 'Refer & Earn | LSA Command' },
  data: () => ({ loading: true, opening: false, error: '', copied: false, account: {} }),
  mounted() { this.load() },
  methods: {
    onCopied() { this.copied = true },
    async openPortal() {
      this.opening = true
      const portal = window.open('about:blank', '_blank')
      if (portal) portal.opener = null
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/agency/affiliate-dashboard`, {}, {
          headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt }
        })
        if (portal) portal.location.replace(response.data.dashboard_url)
        else window.location.assign(response.data.dashboard_url)
      } catch (error) {
        if (portal) portal.close()
        this.error = 'Unable to open your earnings portal. Please try again.'
      } finally { this.opening = false }
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_ENDPOINT}/agency/affiliate-dashboard`, {}, {
          headers: { Authorization: 'Bearer ' + this.$store.state.auth.jwt }
        })
        this.account = response.data
      } catch (error) {
        this.error = error.response && error.response.status === 403
          ? 'This program is available to LSA Command agency owners.'
          : ((error.response && error.response.data.message) || 'Unable to load your referral account. Please try again.')
      } finally { this.loading = false }
    }
  }
}
</script>

<style scoped>
.affiliate-page { color: #374151; }
.affiliate-intro { display: flex; align-items: center; gap: 16px; padding: 20px 24px; margin-bottom: 20px; border: 1px solid #dedfec; border-radius: 4px; background: #f5f5fa; }
.affiliate-intro > i { font-size: 28px; line-height: 1; color: #44468f; flex: 0 0 auto; }
.affiliate-intro strong { font-size: 15px; color: #44468f; }
.affiliate-intro p { margin: 4px 0 0; font-size: 13px; line-height: 1.6; color: #4b5563; }
.affiliate-card { background: #fff; padding: 24px; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
.affiliate-card h2 { font-size: 15px; font-weight: 600; color: #374151; margin: 0 0 10px; }
.affiliate-card p { font-size: 13px; color: #4b5563; line-height: 1.6; }
.affiliate-copy { display: flex; gap: 12px; margin: 18px 0; }
.affiliate-copy input { min-width: 0; flex: 1; border: 1px solid #d1d5db; background: #fff; color: #374151; border-radius: 4px; padding: 10px 12px; font-size: 13px; }
.affiliate-button { display: inline-block; background: #44468f; color: #fff !important; border-radius: 4px; padding: 10px 16px; font-weight: 600; font-size: 12px; text-decoration: none; white-space: nowrap; }
.affiliate-button:hover { background: #363873; }
.affiliate-button:focus-visible { outline: 3px solid #a5b4fc; outline-offset: 3px; }
.affiliate-button:disabled { opacity: .65; cursor: wait; }
.affiliate-note { font-size: 12px !important; color: #6b7280 !important; line-height: 1.6; }
.affiliate-portal { display: flex; align-items: center; gap: 24px; justify-content: space-between; }
@media (max-width: 640px) {
  .affiliate-intro, .affiliate-card { padding: 18px; }
  .affiliate-copy, .affiliate-portal { flex-direction: column; align-items: stretch; }
}
</style>
